import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CardContent } from "@/shared/ui/Card";
import { AvatarWithCounterBadge } from "@/shared/ui/Avatar";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { filter, map, orderBy, some, take } from "lodash";
import { Card } from "@/shared/ui/Card";
import { Row } from "@/shared/ui/Blocks";
import { Touchable } from "@/shared/ui/Touchable";
import { SubtitleText } from "@/shared/ui/Typography";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";
import { Person } from "@/features/people/graphql/generated";
import { cn } from "@/shared/utils/shadcn";
import { TeamType } from "@/features/teams/hooks/useTeams";
import { Skeleton } from "@/shared/ui";
import { getIsSurveysCompleted } from "@/shared/utils/selectors/getSurveyCompletion";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

interface TeamCardProps {
  team: TeamType;
  loading?: boolean;
  className?: string;
}

interface TeamMemberStatusProps {
  memberCount: number;
  profilesIncomplete: number;
}

function TeamMemberStatus({
  memberCount,
  profilesIncomplete,
}: TeamMemberStatusProps) {
  const { t } = useTranslation();

  if (memberCount === 0) {
    return (
      <div aria-label="upperLine">
        <SubtitleText>{t("team.waiting_for_members")}</SubtitleText>
      </div>
    );
  }

  return (
    <div aria-label="upperLine">
      <SubtitleText>
        {t("team.member_count", { count: memberCount })}
      </SubtitleText>
      {profilesIncomplete > 0 && (
        <SubtitleText className="text-orange-400">
          {t("team.profiles_incomplete", { count: profilesIncomplete })}
        </SubtitleText>
      )}
    </div>
  );
}

function TeamCard({ team, loading, className }: TeamCardProps) {
  const navigate = useOrgNavigate();

  if (!team) return null;

  const profilesIncomplete = filter(
    team.members,
    (member) =>
      !getIsSurveysCompleted(
        member?.completed_dim_types as AssessmentSurveyType[],
        team.config?.dimensions || []
      )
  ).length;

  const MemberAvatars = useCallback(({ members }: { members: Person[] }) => {
    const orderedMembers = orderBy(members, ["name", "avatar"]);
    const visibleMembers = take(orderedMembers, 5);

    return (
      <Row className="-space-x-2">
        {map(visibleMembers, (member, index) => (
          <AvatarWithInitials
            key={member.id}
            initials={member.initials || ""}
            avatar={member.avatar || ""}
            size="sm"
          />
        ))}
        {visibleMembers.length < orderedMembers.length && (
          <AvatarWithCounterBadge
            avatar={orderedMembers[5].avatar || ""}
            initials={orderedMembers[5].initials || ""}
            count={orderedMembers.length - 6}
            size="sm"
            className="w-8 h-8"
          />
        )}
      </Row>
    );
  }, []);

  return (
    <Touchable onClick={() => navigate(`/teams/${team.id}`)}>
      <Card className="w-full h-full flex flex-col justify-between border-t-4 border-t-primary min-h-[150px]">
        <CardContent
          className={cn("flex flex-1 flex-col space-y-2", className)}
        >
          <MemberAvatars members={team?.members} />

          <h5>{team.name}</h5>

          {loading ? (
            <Skeleton className="w-full h-4 mt-4" />
          ) : (
            <TeamMemberStatus
              memberCount={team.members.length}
              profilesIncomplete={profilesIncomplete}
            />
          )}
        </CardContent>
      </Card>
    </Touchable>
  );
}

export default TeamCard;
export { TeamCard };
